import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { compose, keyBy, mapValues, values } from 'lodash/fp'
import { Helmet } from 'react-helmet'

import Layout from '../../components/Layout'
import Button from '../../components/Button'
import Heading from '../../components/Heading'
import Table from '../../components/Table'

import { AppContext } from '../App'
import filtersIcon from '../../images/filtersIcon.svg'
import FiltersForm from './Form'
import downloadIcon from '../../images/downloadIcon.svg'
import alertIcon from '../../images/alertIcon.svg'
import { normalizeSortState } from '../../components/Table/convertSortState'
import { downloadFileFromBlob, formatBytes } from '../../utils/download'
import withLoading from '../../utils/withLoading'
import { withNotifier } from '../Notifications'
import withPermissions from '../../containers/withPermissions'
import { stateLabels, stateImages } from './constants'
import Modal, { ReportGenerateModal } from './Modal'

import withQueryParams from '../../utils/withQueryParams'

const decorate = compose(withPermissions, withQueryParams, withNotifier)

const REPORTS_ENDPOINT = '/report/rest/report/search'
const REPORT_ENDPOINT = '/report/rest/report/'
const GENERATE_REPORT_ENDPOINT = '/report/rest/report'

const DOWNLOAD_REPORT_ENDPOINT = '/report/rest/report/download/'
const SET_RESET_STATE = '/report/rest/report/reset/'

const LoadingTable = withLoading(Table)

const normalizeDate = (date) => moment(date).format('YYYY-MM-DD')

class ReportsList extends React.Component {
  static contextType = AppContext

  initialDialogState = { isActive: false, id: undefined, message: undefined }

  state = {
    errorDialog: { isActive: false, id: undefined, message: undefined },
    sort: {
      requestDate: 'desc',
    },
    ready: false,
    showGenerateReportDiaglog: false,
    reports: [],
    users: [],
    meta: {
      limit: 20,
      page: 0,
    },
    filters: {
      states: ['REQUEST', 'GENERATING', 'DONE', 'ERROR'],
    },
  }

  onSortedChange = (sort) => {
    this.setState({ sort }, () => {
      this.fetchReports()
    })
  }

  downloadReport = async (id) => {
    try {
      const response = await this.context.withAuth(
        `${DOWNLOAD_REPORT_ENDPOINT}${id}/report.csv`,
        {
          method: 'GET',
          responseType: 'blob',
        }
      )
      return downloadFileFromBlob(response, 'report.csv')
    } catch (e) {
      console.log(e)
    }
  }

  fetchReports = async () => {
    const { filters, meta, sort } = this.state
    this.props.setParams({ filters, meta, sort })
    this.setState({ ready: false })
    try {
      const response = await this.context.withAuth(REPORTS_ENDPOINT, {
        method: 'POST',
        payload: {
          filters: this.state.filters,
          meta: { ...meta, sorts: normalizeSortState(this.state.sort) },
        },
      })

      const reports = response.models
      this.setState({ reports, meta: response.meta })
    } catch (e) {
      console.log(e)
    }
    this.setState({ ready: true })
  }

  fetchReport = async (id) => {
    try {
      const response = await this.context.withAuth(`${REPORT_ENDPOINT}/${id}`, {
        method: 'GET',
      })

      const report = response.model
      const updatedReports = {
        ...keyBy('id', this.state.reports),
        [report.id]: report,
      }

      this.setState({ reports: values(updatedReports) })
    } catch (e) {
      console.log(e)
    }
  }

  onPageChange = (index) => {
    this.setState({ meta: { ...this.state.meta, page: index } }, () =>
      this.fetchReports(false)
    )
  }

  onFiltersChange = (filters) => {
    this.setState({ filters }, () => this.fetchReports())
  }

  toggleErrorDialog = (id, message) => {
    this.setState({
      errorDialog: {
        id,
        message,
        isActive: !this.state.errorDialog.isActive,
      },
    })
  }

  resetReportStatus = async () => {
    try {
      await this.context.withAuth(
        `${SET_RESET_STATE}${this.state.errorDialog.id}`,
        {
          method: 'PUT',
        }
      )

      this.props.notify('Raport zostanie wygenerowany ponownie.', 'success')
      await this.fetchReport(this.state.errorDialog.id)
    } catch (e) {
      this.props.notify(
        'Wystąpił błąd, nie udało się wygenerować raportu. spróbuj ponownie.',
        'error'
      )
    }
    this.setState({ errorDialog: this.initialDialogState })
  }

  generateReport = async () => {
    try {
      const response = await this.context.withAuth(GENERATE_REPORT_ENDPOINT, {
        method: 'POST',
        payload: {},
      })
      if (response.success) {
        this.toggleReportDialog()
      }
    } catch (e) {
      console.log(e)
    }
  }

  toggleReportDialog = () => {
    this.setState({
      showGenerateReportDiaglog: !this.state.showGenerateReportDiaglog,
    })
  }

  componentDidMount() {
    const searchParams = this.props.getParams()
    try {
      const params = mapValues(JSON.parse, searchParams)
      this.setState({ ...this.state, ...params }, () =>
        this.fetchReports(false)
      )
    } catch (e) {
      this.fetchReports()
    }
  }

  render() {
    const { toggleFilters } = this.context
    return (
      <Layout
        filters={
          <FiltersForm
            onSubmit={this.onFiltersChange}
            onClose={toggleFilters}
            initialValues={this.state.filters}
          />
        }
      >
        <Helmet htmlAttributes={{ lang: 'pl-PL' }}>
          <title>Lista raportów</title>
        </Helmet>
        <>
          <Heading title="Lista Raportów">
            <Button
              marginLeft
              icon={downloadIcon}
              label="Nowy Raport"
              onClick={this.generateReport}
              color="white"
              altTag=""
              imgAriaHidden={true}
            />
            <Button
              icon={filtersIcon}
              color="white"
              label="Filtruj"
              onClick={toggleFilters}
              marginLeft
              altTag=""
              imgAriaHidden={true}
            />
          </Heading>
          <LoadingTable
            getTrProps={() => ({
              style: { display: 'flex', alignItems: 'center' },
            })}
            defaultPageSize={this.state.meta.limit}
            ready={this.state.ready}
            data={this.state.reports}
            total={this.state.meta.total}
            getLoadingProps={() => ({
              'aria-hidden': true,
              role: 'none',
            })}
            onPageChange={this.onPageChange}
            onSortedChange={this.onSortedChange}
            order={this.state.sort}
            page={this.state.meta.page}
            columns={[
              {
                Header: 'Nr',
                id: 'id',
                accessor: 'id',
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                id: 'requestDate',
                Header: 'Zlecono',
                accessor: ({ requestDate }) => normalizeDate(requestDate),
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                id: 'generationDate',
                accessor: ({ generationDate }) =>
                  generationDate && normalizeDate(generationDate),

                Header: 'Wygenerowano',
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },

              {
                id: 'state',
                Cell: ({ original: { state } }) => (
                  <>
                    <Img src={stateImages[state]} alt="" aria-hidden="true" />
                    <span style={{ marginLeft: '5px' }}>
                      {stateLabels[state]}
                    </span>
                  </>
                ),
                Header: 'Status',
                sortable: true,
              },
              {
                id: 'size',
                accessor: ({ size }) => `${formatBytes(size, 0)}`,
                Header: 'Rozmiar',
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                sortable: false,
                Cell: ({ original: { state, id, errorDescription } }) => {
                  if (state === 'DONE') {
                    return (
                      <React.Fragment>
                        <DownloadCell onClick={() => this.downloadReport(id)}>
                          <Img
                            alt=""
                            src={downloadIcon}
                            height={16}
                            aria-hidden="true"
                          />
                          <span style={{ marginLeft: '5px' }}>
                            Pobierz wyniki
                          </span>
                        </DownloadCell>
                      </React.Fragment>
                    )
                  } else if (state === 'ERROR') {
                    return (
                      <DownloadCell
                        title={errorDescription}
                        onClick={() =>
                          this.toggleErrorDialog(id, errorDescription)
                        }
                      >
                        <Img
                          alt=""
                          aria-hidden="true"
                          src={alertIcon}
                          title={errorDescription}
                          height={16}
                        />
                        <span style={{ marginLeft: '5px' }}>Pokaż błąd</span>
                      </DownloadCell>
                    )
                  }
                  return null
                },
              },
            ]}
          />
        </>
        <Modal
          isOpen={this.state.errorDialog.isActive}
          onClose={this.toggleErrorDialog}
          message={this.state.errorDialog.message}
          onReset={this.resetReportStatus}
        />
        <ReportGenerateModal
          isOpen={this.state.showGenerateReportDiaglog}
          onClose={this.toggleReportDialog}
        />
      </Layout>
    )
  }
}

const DownloadCell = styled(Button)`
  display: flex;
  height: 1.3rem;
  flex-direction: row;
  align-items: center;
  width: auto;
  color: black;
  background: inherit;
  border: none;
  &:hover {
    color: var(--color-primary);
    background: inherit;
  }
`

const Img = styled.img`
  margin-bottom: 0;
  cursor: pointer;
  height: ${(props) => (props.height ? `16px` : `22px`)};
`

export default decorate(ReportsList)
